import { listCountsHook } from "_shared/_api/list-counts/list-counts-hook";
import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import ActiveTeamSchema, {
  ActiveTeamJSON,
  ActiveTeamPerspective,
} from "work/work-agreements/teams/active-team/_schemas/active-team-schema";
import z from "zod";

/**
 * The columns `work/active-teams` sorts on, spelled as the API's `ActiveTeamOrderField` is. A team
 * has no name of its own, so its work's name is the alphabetical order.
 */
export type ActiveTeamOrderField = "WorkName" | "HireDate";

/** Which way a column is sorted, spelled as the API's `OrderDirection` is. */
export type ActiveTeamOrderDirection = "Ascending" | "Descending";

/**
 * What one page of active teams is asked for with (#5441, #6557).
 *
 * Every field goes to the server. Narrowing or sorting a fetched page in the browser would only
 * ever rearrange that page, which is the defect paging was added to remove.
 */
export type ActiveTeamListQuery = {
  /** Only the teams listed for this reason. Every active team of the reader's when absent. */
  perspective?: ActiveTeamPerspective;
  /** Matched against the work's name and the team leader's name, in the store. */
  search?: string;
  /** Which column to sort on. The work's name when absent. */
  orderBy?: ActiveTeamOrderField;
  /** Which way to sort it. Ascending when absent. */
  orderDir?: ActiveTeamOrderDirection;
  pageIndex?: number;
  pageSize?: number;
};

/** The query string for one page of active teams. A blank search is left off rather than sent. */
export function activeTeamListParams(query: ActiveTeamListQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.perspective) params.set("perspective", query.perspective);
  const search = query.search?.trim();
  if (search) params.set("search", search);
  if (query.orderBy) params.set("orderBy", query.orderBy);
  if (query.orderDir) params.set("orderDir", query.orderDir);
  if (query.pageIndex !== undefined) params.set("pageIndex", `${query.pageIndex}`);
  if (query.pageSize !== undefined) params.set("pageSize", `${query.pageSize}`);
  return params;
}

/** One page of active teams, with the totals the X-Pagination header carries. */
function toActiveTeamPage(
  response: ActiveTeamJSON[],
  meta?: { response?: { headers: { get(name: string): string | null } } },
): Paginated<ActiveTeamJSON> {
  return {
    data: response,
    headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
  };
}

const count = z.number().int().nonnegative();

/**
 * The Teams tab's badge and its perspective chips' counts, from `GET work/active-teams/counts` (#6709):
 * every active team of the reader's, and those listed for each reason. A team listed for both reasons is
 * counted under each chip and once in the total.
 */
export const ActiveTeamCountsJSONZ = z.object({
  total: count,
  on: count,
  workingForMe: count,
});

export type ActiveTeamCounts = z.infer<typeof ActiveTeamCountsJSONZ>;

/** The counts' query string: the search alone, since the perspective is what they count. Blank is none. */
export function activeTeamCountsParams(search: string | undefined): URLSearchParams {
  const params = new URLSearchParams();
  const trimmed = search?.trim();
  if (trimmed) params.set("search", trimmed);
  return params;
}

export const teamsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getActiveTeams: build.query<Paginated<ActiveTeamJSON>, ActiveTeamListQuery>({
      query: (query) => ({ url: "work/active-teams", params: activeTeamListParams(query) }),
      rawResponseSchema: z.array(ActiveTeamSchema.ActiveTeamJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: toActiveTeamPage,
    }),
    getActiveTeamCounts: build.query<ActiveTeamCounts, { search?: string }>({
      query: ({ search }) => ({ url: "work/active-teams/counts", params: activeTeamCountsParams(search) }),
      rawResponseSchema: ActiveTeamCountsJSONZ,
      onSchemaFailure: logSchemaFailure,
    }),
  }),
});

export const { useGetActiveTeamsQuery, useGetActiveTeamCountsQuery } = teamsApi;

export const useActiveTeamCounts = listCountsHook<{ search?: string }, ActiveTeamCounts>(useGetActiveTeamCountsQuery);
